/**
 * Press-mode geometry for the `/scan` heat-press kiosk (pure — no React, no DOM, no IO).
 *
 * The idea, in one line: the heat press's laser guides are FIXED lines on the platen — they do not move
 * easily, and a run presses dozens of designs against the same lines. So the kiosk mirrors the lasers
 * exactly where they sit (configured once per press, by dragging or typing inches), and every scanned
 * design is HUNG ON THEM: its top edge on the "back top" laser, its centre on the "centre" laser, and so
 * on. Nothing to configure per design. The picture shows which way is up, which ink touches which laser,
 * and where the far edge lands (the fit check). The placement RULES below are only the fallback for a
 * side no laser has been assigned to.
 *
 * Coordinate frames, because the mirror bug lives here:
 *  · Rules are authored in the WEARER's frame (`fromCenterIn` > 0 = toward the wearer's left).
 *  · `placeDesign` returns the box in the COLLAR frame: x from the viewer's left edge, y from the
 *    platen edge the collar seam sits on — as if that edge were at the top of the screen.
 *  · `toViewer` rotates the collar frame 180° when the collar sits on the NEAR edge (toward the
 *    operator), so the screen shows what the operator sees standing at the press.
 *
 * Everything here is display-only. Nothing in this module touches the press write path — the press bar
 * keeps its handler and its guards; this module only decides where to draw a picture around it.
 */
import type { ApparelKind, SizeBand } from '@fsp/contracts';
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Platens
// ─────────────────────────────────────────────────────────────────────────────

export interface Platen {
  id: string;
  /** Operator-facing name, shown on the rail chip and in the picker. */
  name: string;
  /** Left ↔ right, as the operator faces the press. */
  acrossIn: number;
  /** Far ↔ near — the shirt's collar→hem axis when it is threaded onto the platen. */
  deepIn: number;
}

/**
 * The Hotronix quick-change catalog (Stahls'/Transfer Express platen pages, 2026). The 16 × 20 is seeded
 * BOTH ways because no vendor page says how it is mounted, and the frame's portrait/landscape follows
 * the mount (plan D1) — the owner keeps the one that matches the press. Custom dimensions are allowed
 * through `customPlaten`.
 */
export const HOTRONIX_PLATENS: readonly Platen[] = [
  { id: 'hx-16x20', name: 'Hotronix 16 × 20', acrossIn: 16, deepIn: 20 },
  { id: 'hx-20x16', name: 'Hotronix 16 × 20 · long side across', acrossIn: 20, deepIn: 16 },
  { id: 'hx-16x16', name: 'Hotronix 16 × 16', acrossIn: 16, deepIn: 16 },
  { id: 'hx-11x15', name: 'Hotronix 11 × 15', acrossIn: 11, deepIn: 15 },
  { id: 'hx-8x10', name: 'Hotronix 8 × 10', acrossIn: 8, deepIn: 10 },
  { id: 'hx-6x10', name: 'Hotronix 6 × 10', acrossIn: 6, deepIn: 10 },
  { id: 'hx-6x6', name: 'Hotronix 6 × 6', acrossIn: 6, deepIn: 6 },
  { id: 'hx-6x20', name: 'Hotronix 6 × 20 · sleeve / leg', acrossIn: 6, deepIn: 20 },
  { id: 'hx-4x4', name: 'Hotronix 4 × 4', acrossIn: 4, deepIn: 4 },
];

export const DEFAULT_PLATEN_ID = 'hx-16x20';
export const MAX_PLATEN_IN = 40;
export const PLATEN_STEP_IN = 0.5;

/** A custom platen from two dimensions, or `null` when they are not a sane platen (0.5 in steps, ≤ 40 in). */
export function customPlaten(acrossIn: number, deepIn: number): Platen | null {
  const ok = (n: number) =>
    Number.isFinite(n) &&
    n > 0 &&
    n <= MAX_PLATEN_IN &&
    Math.abs(n / PLATEN_STEP_IN - Math.round(n / PLATEN_STEP_IN)) < 1e-9;
  if (!ok(acrossIn) || !ok(deepIn)) return null;
  return {
    id: `custom-${fmtIn(acrossIn)}x${fmtIn(deepIn)}`,
    name: `Custom ${fmtIn(acrossIn)} × ${fmtIn(deepIn)}`,
    acrossIn,
    deepIn,
  };
}

/** Resolve a stored platen id against the catalog (+ an optional remembered custom platen). Falls back
 *  to the default so an unknown / stale id can never leave the frame without a platen. */
export function platenById(id: string | null | undefined, custom?: Platen | null): Platen {
  if (custom && id === custom.id) return custom;
  return (
    HOTRONIX_PLATENS.find((p) => p.id === id) ??
    // Non-null: DEFAULT_PLATEN_ID is one of the catalog ids above, asserted in press-guide-lib.test.ts.
    (HOTRONIX_PLATENS.find((p) => p.id === DEFAULT_PLATEN_ID) as Platen)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// How the garment is loaded
// ─────────────────────────────────────────────────────────────────────────────

/** Which platen edge the collar seam sits on when a shirt is threaded (plan D2). */
export type CollarEdge = 'far' | 'near';

export interface LoadRule {
  collarEdge: CollarEdge;
  /** Distance from that platen edge to the collar seam, in inches. 0 = the seam is butted to the edge. */
  collarInsetIn: number;
}

export const DEFAULT_LOAD_RULE: LoadRule = { collarEdge: 'far', collarInsetIn: 0 };

// ─────────────────────────────────────────────────────────────────────────────
// Placement rules — where a print sits on the garment
// ─────────────────────────────────────────────────────────────────────────────

export type VerticalAnchor = 'top' | 'center' | 'bottom';

export interface PlacementRule {
  /** Print location slug (`front`, `back`, `left-chest`, …) — the same slugs the catalog mapping uses. */
  location: string;
  sizeBand: SizeBand;
  /** Absent = applies to any apparel kind; a rule naming one wins over one that does not. */
  apparelKind?: ApparelKind;
  /** `top` / `center`: the design's top edge / centre sits `offsetIn` below the collar seam.
   *  `bottom`: the design's bottom edge sits `offsetIn` above the NEAR platen edge (sleeves, hems). */
  vertical: { anchor: VerticalAnchor; offsetIn: number };
  /** WEARER frame: the design's centre, inches from the garment's centre line, positive toward the
   *  wearer's LEFT. A left-chest print is positive; the mirror-image right-chest is negative. */
  horizontal: { fromCenterIn: number };
}

/**
 * ⚠️ FALLBACK ONLY. Since the fixed-laser revision, a design's position comes from the lasers it hangs on
 * (`placeOnLasers`); these industry numbers (ScreenPrinting.com, Ninja Transfers, DTF Sheet, DTF Virginia,
 * 2026) apply only to an axis no laser has been assigned to, and the readouts say so when they do. They
 * are NOT the shop's measured numbers.
 */
export const SEED_PLACEMENT_RULES: readonly PlacementRule[] = [
  {
    location: 'front',
    sizeBand: 'adult',
    vertical: { anchor: 'top', offsetIn: 3 },
    horizontal: { fromCenterIn: 0 },
  },
  {
    location: 'front',
    sizeBand: 'youth',
    vertical: { anchor: 'top', offsetIn: 2 },
    horizontal: { fromCenterIn: 0 },
  },
  // Hoodie fronts sit lower (the hood covers art placed higher) and must clear the pocket.
  {
    location: 'front',
    sizeBand: 'adult',
    apparelKind: 'hoodie',
    vertical: { anchor: 'top', offsetIn: 3.75 },
    horizontal: { fromCenterIn: 0 },
  },
  {
    location: 'front',
    sizeBand: 'youth',
    apparelKind: 'hoodie',
    vertical: { anchor: 'top', offsetIn: 2.75 },
    horizontal: { fromCenterIn: 0 },
  },
  {
    location: 'back',
    sizeBand: 'adult',
    vertical: { anchor: 'top', offsetIn: 4 },
    horizontal: { fromCenterIn: 0 },
  },
  {
    location: 'back',
    sizeBand: 'youth',
    vertical: { anchor: 'top', offsetIn: 3 },
    horizontal: { fromCenterIn: 0 },
  },
  {
    location: 'left-chest',
    sizeBand: 'adult',
    vertical: { anchor: 'center', offsetIn: 3 },
    horizontal: { fromCenterIn: 3.75 },
  },
  {
    location: 'left-chest',
    sizeBand: 'youth',
    vertical: { anchor: 'center', offsetIn: 2.5 },
    horizontal: { fromCenterIn: 3 },
  },
  {
    location: 'right-chest',
    sizeBand: 'adult',
    vertical: { anchor: 'center', offsetIn: 3 },
    horizontal: { fromCenterIn: -3.75 },
  },
  {
    location: 'right-chest',
    sizeBand: 'youth',
    vertical: { anchor: 'center', offsetIn: 2.5 },
    horizontal: { fromCenterIn: -3 },
  },
  // Sleeves press on the 6 × 20 sleeve platen; "collar edge" is then the shoulder end of the sleeve.
  {
    location: 'sleeve',
    sizeBand: 'adult',
    vertical: { anchor: 'top', offsetIn: 1.5 },
    horizontal: { fromCenterIn: 0 },
  },
  {
    location: 'sleeve',
    sizeBand: 'youth',
    vertical: { anchor: 'top', offsetIn: 1 },
    horizontal: { fromCenterIn: 0 },
  },
];

/**
 * The rule for a placement: the most specific match wins — (location, band, apparelKind) over
 * (location, band) with no kind. `undefined` when the location has no rule at all, and the caller then
 * draws NO guides (a guide from a guessed rule is worse than none).
 */
export function findPlacementRule(
  rules: readonly PlacementRule[],
  location: string,
  sizeBand: SizeBand,
  apparelKind?: ApparelKind,
): PlacementRule | undefined {
  const forLocation = rules.filter((r) => r.location === location && r.sizeBand === sizeBand);
  if (apparelKind) {
    const exact = forLocation.find((r) => r.apparelKind === apparelKind);
    if (exact) return exact;
  }
  return forLocation.find((r) => r.apparelKind === undefined);
}

// ─────────────────────────────────────────────────────────────────────────────
// Geometry
// ─────────────────────────────────────────────────────────────────────────────

/** A box in platen inches. `x` from the viewer's LEFT edge; `y` from the COLLAR edge (collar frame) or
 *  from the TOP of the screen (viewer frame) — `toViewer` converts one to the other. */
export interface InchBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DesignSize {
  widthIn: number;
  heightIn: number;
}

/**
 * Which way the wearer's left points on screen. Every print the operator sees while the shirt faces
 * them (front, left-chest, right-chest, sleeve, …) is face-up: the wearer's left is the VIEWER'S RIGHT,
 * so `+1`. The back is the one location where they coincide, so `−1`.
 */
export function viewerSign(location: string): 1 | -1 {
  return location === 'back' ? -1 : 1;
}

/** Place a design on the platen by its rule, in the collar frame (collar edge at y = 0). */
export function placeDesign(
  rule: PlacementRule,
  load: LoadRule,
  platen: Platen,
  design: DesignSize,
): InchBox {
  const { anchor, offsetIn } = rule.vertical;
  let y: number;
  if (anchor === 'top') y = load.collarInsetIn + offsetIn;
  else if (anchor === 'center') y = load.collarInsetIn + offsetIn - design.heightIn / 2;
  else y = platen.deepIn - offsetIn - design.heightIn; // bottom: up from the near edge
  const xCenter = platen.acrossIn / 2 + viewerSign(rule.location) * rule.horizontal.fromCenterIn;
  return { x: xCenter - design.widthIn / 2, y, w: design.widthIn, h: design.heightIn };
}

/** Collar frame → what the operator sees: unchanged for a far collar, rotated 180° for a near one. */
export function toViewer(box: InchBox, platen: Platen, collarEdge: CollarEdge): InchBox {
  if (collarEdge === 'far') return box;
  return {
    x: platen.acrossIn - (box.x + box.w),
    y: platen.deepIn - (box.y + box.h),
    w: box.w,
    h: box.h,
  };
}

export interface Overflow {
  edge: 'top' | 'bottom' | 'left' | 'right';
  /** How far past the platen edge the design reaches, in inches. */
  byIn: number;
}

/** An overflow smaller than this is reported as a fit: 0.05 in (~1 mm) is under any film-cutting
 *  tolerance, and a 17.01 in file on a 20 in platen would otherwise warn about 0.01 in forever. */
export const FIT_TOLERANCE_IN = 0.05;

/** Every platen edge the design crosses by more than the tolerance (collar frame). Empty = it fits. */
export function fitsPlaten(box: InchBox, platen: Platen): Overflow[] {
  const over: Overflow[] = [];
  const t = FIT_TOLERANCE_IN;
  if (box.y < -t) over.push({ edge: 'top', byIn: -box.y });
  if (box.y + box.h > platen.deepIn + t)
    over.push({ edge: 'bottom', byIn: box.y + box.h - platen.deepIn });
  if (box.x < -t) over.push({ edge: 'left', byIn: -box.x });
  if (box.x + box.w > platen.acrossIn + t)
    over.push({ edge: 'right', byIn: box.x + box.w - platen.acrossIn });
  return over;
}

/**
 * Where to draw a design whose print size is UNKNOWN (a screen-print side, or an API older than the
 * `widthIn`/`heightIn` fields): centred, 60 % of the platen's depth tall, width from the image's aspect
 * (default = the common 14 × 17 transfer), clamped inside the platen. Drawn without guides — a guide on a
 * guessed size would be a confident lie.
 */
export function fallbackBox(platen: Platen, aspect = 14 / 17): InchBox {
  let h = platen.deepIn * 0.6;
  let w = h * aspect;
  if (w > platen.acrossIn * 0.9) {
    w = platen.acrossIn * 0.9;
    h = w / aspect;
  }
  return { x: (platen.acrossIn - w) / 2, y: (platen.deepIn - h) / 2, w, h };
}

/** Inches → CSS percentages of the platen frame (viewer frame). */
export function boxToPercent(
  box: InchBox,
  platen: Platen,
): { left: string; top: string; width: string; height: string } {
  const pct = (n: number, d: number) => `${((100 * n) / d).toFixed(3)}%`;
  return {
    left: pct(box.x, platen.acrossIn),
    top: pct(box.y, platen.deepIn),
    width: pct(box.w, platen.acrossIn),
    height: pct(box.h, platen.deepIn),
  };
}

/** The frame's CSS: true platen proportion, as large as the container allows (container-query units,
 *  the same trick the Classic tile uses for its 14 × 17 well). */
export function platenFrameStyle(platen: Platen): { width: string; aspectRatio: string } {
  return {
    width: `min(100%, calc(100cqh * ${platen.acrossIn} / ${platen.deepIn}))`,
    aspectRatio: `${platen.acrossIn} / ${platen.deepIn}`,
  };
}

/** "4", "3.75", "13.57" — two decimals, trailing zeros dropped. */
export function fmtIn(n: number): string {
  return n.toFixed(2).replace(/\.?0+$/, '');
}

// ─────────────────────────────────────────────────────────────────────────────
// Lasers — the FIXED lines on the press; designs hang on them
// ─────────────────────────────────────────────────────────────────────────────

export type LaserId = 'l1' | 'l2' | 'l3' | 'l4';
export const LASER_IDS: readonly LaserId[] = ['l1', 'l2', 'l3', 'l4'];
export type LaserOrientation = 'h' | 'v';
/** Which feature of the DESIGN sits on a laser: an `h` laser takes a horizontal feature (top / centre /
 *  bottom edge), a `v` laser a vertical one (left / centre / right edge). */
export type DesignAnchor = 'top' | 'centerH' | 'bottom' | 'left' | 'centerV' | 'right';
export const H_ANCHORS: readonly DesignAnchor[] = ['top', 'centerH', 'bottom'];
export const V_ANCHORS: readonly DesignAnchor[] = ['left', 'centerV', 'right'];
/** The location tag meaning "every print location". */
export const ANY_LOCATION = '*';
/** The locations the setup sheet offers as tags — the catalog's placement slugs. */
export const LASER_LOCATIONS: readonly string[] = [
  'front',
  'back',
  'left-chest',
  'right-chest',
  'sleeve',
];

export const LaserSchema = z.object({
  id: z.enum(['l1', 'l2', 'l3', 'l4']),
  /** Operator-facing name: "Centre", "Back top", … */
  label: z.string().max(24),
  orientation: z.enum(['h', 'v']),
  /** COLLAR frame, inches: an `h` laser from the collar edge; a `v` laser from the collar-frame left
   *  edge (the operator's left when the collar is far). */
  atIn: z.number().min(0).max(MAX_PLATEN_IN),
  /** A laser that is physically switched off is kept in the setup but neither drawn nor used. */
  enabled: z.boolean(),
  anchor: z.enum(['top', 'centerH', 'bottom', 'left', 'centerV', 'right']),
  /** Print locations this laser positions (`*` = any). Empty = drawn as a reference line only. */
  usedFor: z.array(z.string().max(40)).max(8),
});
export type Laser = z.infer<typeof LaserSchema>;

/**
 * A starting setup for a press nobody has configured yet: a centre line, a horizontal for backs, one
 * for fronts (half an inch apart so the two lines read as two), and a vertical for left chests —
 * positions are placeholders to DRAG, not measurements.
 * Centred lines derive from the platen so they start in the middle of whatever is mounted.
 */
export function defaultLasers(platen: Platen): Laser[] {
  const mid = snapIn(platen.acrossIn / 2);
  return [
    {
      id: 'l1',
      label: 'Centre',
      orientation: 'v',
      atIn: mid,
      enabled: true,
      anchor: 'centerV',
      usedFor: ['front', 'back', 'sleeve'],
    },
    {
      id: 'l2',
      label: 'Back top',
      orientation: 'h',
      atIn: 3,
      enabled: true,
      anchor: 'top',
      usedFor: ['back'],
    },
    {
      id: 'l3',
      label: 'Front top',
      orientation: 'h',
      atIn: 2.5,
      enabled: true,
      anchor: 'top',
      usedFor: ['front'],
    },
    {
      id: 'l4',
      label: 'Left chest',
      orientation: 'v',
      atIn: Math.min(platen.acrossIn, snapIn(platen.acrossIn / 2 + 3.75)),
      enabled: true,
      anchor: 'centerV',
      usedFor: ['left-chest'],
    },
  ];
}

/** A laser's anchor, coerced to its orientation (a stored `h` laser can never carry a `v` anchor). */
export function laserAnchor(laser: Pick<Laser, 'orientation' | 'anchor'>): DesignAnchor {
  if (laser.orientation === 'h') return H_ANCHORS.includes(laser.anchor) ? laser.anchor : 'top';
  return V_ANCHORS.includes(laser.anchor) ? laser.anchor : 'centerV';
}

/** Does this laser position prints at `location`? Off lasers position nothing. */
export function laserAppliesTo(laser: Laser, location: string): boolean {
  return (
    laser.enabled && (laser.usedFor.includes(ANY_LOCATION) || laser.usedFor.includes(location))
  );
}

export interface LaserPlacement {
  box: InchBox;
  /** The laser that fixed the design vertically / horizontally; absent = that axis fell back. */
  hLaser?: Laser;
  vLaser?: Laser;
}

/**
 * Hang a design on the fixed lasers (collar frame). Per axis, the FIRST enabled laser of that
 * orientation tagged for the location wins; an axis with no laser falls back to the placement rule
 * (or, with no rule either, to 3 in below the collar / centred on the platen) — and the caller says so
 * in the readouts, because a fallback is a guess the operator must know about.
 */
/**
 * The part of a design file the guides should line up with, as fractions of the file box
 * (docs/design-alignment-plan-v1.md § 3.1). Absent is `WHOLE_FILE`, the identity case.
 */
export interface AlignmentBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Unset alignment. Anchoring to this reduces, algebraically, to anchoring to the whole file. */
export const WHOLE_FILE_ALIGNMENT: AlignmentBox = { x: 0, y: 0, w: 1, h: 1 };

/** Whether a box narrows anything — the press only draws one that does. */
export function isWholeFileAlignment(a: AlignmentBox): boolean {
  const EPS = 1e-9;
  return a.x <= EPS && a.y <= EPS && a.w >= 1 - EPS && a.h >= 1 - EPS;
}

export function placeOnLasers(
  lasers: readonly Laser[],
  location: string,
  platen: Platen,
  size: DesignSize,
  load: LoadRule,
  fallbackRule?: PlacementRule,
  /**
   * Which part of the file hangs on the beams. Design files are trimmed to ink, so a satellite element
   * — a logo in a corner, a wordmark down one edge — stretches the file box and drags the artwork off
   * the centre line when the box is what gets centred. Absent = the whole file = today's numbers.
   */
  align: AlignmentBox = WHOLE_FILE_ALIGNMENT,
): LaserPlacement {
  const hLaser = lasers.find((l) => l.orientation === 'h' && laserAppliesTo(l, location));
  const vLaser = lasers.find((l) => l.orientation === 'v' && laserAppliesTo(l, location));
  const fb = fallbackRule
    ? placeDesign(fallbackRule, load, platen, size)
    : {
        x: (platen.acrossIn - size.widthIn) / 2,
        y: load.collarInsetIn + 3,
        w: size.widthIn,
        h: size.heightIn,
      };
  let { x, y } = fb;
  // Each anchor resolves against the ALIGNMENT box, then the whole file is positioned so that box
  // lands where the anchor says. With `align` unset (x 0, w 1) these reduce algebraically to the
  // previous formulas — `at − 0`, `at − w/2`, `at − w` — so there is no branch and no legacy path.
  if (hLaser) {
    const a = laserAnchor(hLaser);
    const frac =
      a === 'top' ? align.y : a === 'centerH' ? align.y + align.h / 2 : align.y + align.h;
    y = hLaser.atIn - frac * size.heightIn;
  }
  if (vLaser) {
    const a = laserAnchor(vLaser);
    const frac =
      a === 'left' ? align.x : a === 'centerV' ? align.x + align.w / 2 : align.x + align.w;
    x = vLaser.atIn - frac * size.widthIn;
  }
  return { box: { x, y, w: size.widthIn, h: size.heightIn }, hLaser, vLaser };
}

export interface LaserLine {
  id: LaserId;
  label: string;
  orientation: LaserOrientation;
  /** COLLAR frame, inches. */
  at: number;
  /** On SCREEN, inches — what the renderer positions by, and what the "from left edge" readout reports
   *  so it is always the OPERATOR's left. Equals `at` for a far collar. */
  atViewer: number;
  /** "3 in from collar edge" / "8 in from left edge". */
  readout: string;
  /** This laser positions the side on screen (drawn bright); the others are drawn dim. */
  used: boolean;
}

/** The enabled lasers that fall on the platen, as drawable lines with readouts. */
export function laserLines(
  lasers: readonly Laser[],
  platen: Platen,
  collarEdge: CollarEdge,
  usedIds: ReadonlySet<string>,
): LaserLine[] {
  const out: LaserLine[] = [];
  for (const l of lasers) {
    if (!l.enabled) continue;
    const extent = l.orientation === 'h' ? platen.deepIn : platen.acrossIn;
    if (l.atIn < -1e-9 || l.atIn > extent + 1e-9) continue;
    const atViewer = collarEdge === 'far' ? l.atIn : extent - l.atIn;
    out.push({
      id: l.id,
      label: l.label,
      orientation: l.orientation,
      at: l.atIn,
      atViewer,
      readout:
        l.orientation === 'h'
          ? `${fmtIn(l.atIn)} in from collar edge`
          : `${fmtIn(atViewer)} in from left edge`,
      used: usedIds.has(l.id),
    });
  }
  return out;
}

/** Drag/typing resolution for a laser position. */
export const LASER_SNAP_IN = 0.25;

export function snapIn(n: number, step = LASER_SNAP_IN): number {
  return Math.round(n / step) * step;
}

/**
 * A pointer over the frame → the laser's COLLAR-frame inches, snapped and clamped to the platen.
 * `px` is the pointer's clientX (a `v` laser) or clientY (an `h`), `rectStart`/`rectSize` the frame's
 * bounding box on that axis, `flipped` true for a near collar (the screen is the collar frame rotated).
 */
export function pointerToInches(
  px: number,
  rectStart: number,
  rectSize: number,
  platenExtentIn: number,
  flipped: boolean,
): number {
  if (rectSize <= 0) return 0;
  const frac = Math.min(1, Math.max(0, (px - rectStart) / rectSize));
  const viewerIn = frac * platenExtentIn;
  const collarIn = flipped ? platenExtentIn - viewerIn : viewerIn;
  return Math.min(platenExtentIn, Math.max(0, snapIn(collarIn)));
}

// ─────────────────────────────────────────────────────────────────────────────
// Pager — which side is on screen
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pager order: the front family first (`front`, then the other face-up prints alphabetically), the
 * back last — the order the operator meets them, and the order the run is pressed in. The resolver
 * sorts alphabetically (back before front), which is fine for two side-by-side tiles but wrong for a
 * pager that opens on the first entry.
 */
export function pagerOrder<T extends { location: string }>(placements: readonly T[]): T[] {
  const rank = (loc: string) => (loc === 'front' ? 0 : loc === 'back' ? 2 : 1);
  return [...placements].sort(
    (a, b) => rank(a.location) - rank(b.location) || a.location.localeCompare(b.location),
  );
}

export interface PagerPlacement {
  location: string;
  method: 'dtf' | 'screen-print';
  pressed: boolean;
}

/**
 * Which side a fresh scan opens on. STICKY first: the location the operator was on for the previous
 * garment, when this one has it — a run presses all fronts then all backs with the lasers set once per
 * side, so the screen must stay on the side being pressed (plan D5). Otherwise the first side that still
 * needs pressing, else the first side.
 */
export function initialSideIndex(
  placements: readonly PagerPlacement[],
  stickyLocation: string | null,
): number {
  if (stickyLocation) {
    const i = placements.findIndex((p) => p.location === stickyLocation);
    if (i >= 0) return i;
  }
  const unpressed = placements.findIndex((p) => p.method === 'dtf' && !p.pressed);
  return unpressed >= 0 ? unpressed : 0;
}

/** The next DTF side after `from` (wrapping) that still needs pressing — `null` when none does. */
export function nextUnpressedIndex(
  placements: readonly PagerPlacement[],
  from: number,
): number | null {
  const n = placements.length;
  for (let step = 1; step < n; step++) {
    const i = (from + step) % n;
    const p = placements[i];
    if (p && p.method === 'dtf' && !p.pressed) return i;
  }
  return null;
}

/**
 * What the NEXT scan should open on, decided at the moment a side is pressed. Two floor flows share one
 * kiosk and they want opposite things: a RUN presses one side of every garment (lasers set once), so the
 * next garment must open on the side just pressed; a per-garment flow presses every side, so the next
 * garment must open on its first side. The garment itself tells them apart — a run leaves it incomplete,
 * a per-garment press completes it. So: the pressed location while the garment still has an unpressed
 * DTF side; nothing once it is complete. (An auto-advance never sets this — only a press or a deliberate
 * pager tap expresses the operator's intent.)
 */
export function stickyAfterPress(
  placementsAfter: readonly PagerPlacement[],
  pressedLocation: string,
): string | null {
  const dtf = placementsAfter.filter((p) => p.method === 'dtf');
  const complete = dtf.length > 0 && dtf.every((p) => p.pressed);
  return complete ? null : pressedLocation;
}

/** Step the pager one side in either direction, wrapping. */
export function stepIndex(length: number, from: number, dir: 1 | -1): number {
  if (length <= 0) return 0;
  return (from + dir + length) % length;
}

/** How long the press bar stays disarmed after an auto-advance, so a double-tap habit cannot press the
 *  side that just slid under the finger (plan D6). */
export const AUTO_ADVANCE_DISARM_MS = 700;

// ─────────────────────────────────────────────────────────────────────────────
// Per-device preferences
// ─────────────────────────────────────────────────────────────────────────────

export const PRESS_PREFS_KEY = 'fsp.scan.press.v1';

const PressPrefsSchema = z.object({
  /** `press` = the platen frame + rail; `classic` = the two-tile layout, kept as the rollback (plan D7). */
  layout: z.enum(['press', 'classic']).default('press'),
  guides: z.boolean().default(true),
  platenId: z.string().default(DEFAULT_PLATEN_ID),
  collarEdge: z.enum(['far', 'near']).default('far'),
  /** A remembered custom platen, when `platenId` names one. */
  customPlaten: z
    .object({ acrossIn: z.number().positive(), deepIn: z.number().positive() })
    .optional(),
  /** The press's laser setup, once the operator has touched it. Absent = `defaultLasers(platen)`. */
  lasers: z.array(LaserSchema).max(4).optional(),
});

export type PressPrefs = z.infer<typeof PressPrefsSchema>;

export const DEFAULT_PRESS_PREFS: PressPrefs = PressPrefsSchema.parse({});

/** The subset of `Storage` the prefs need — injectable so the load/save round-trip is unit-testable. */
export type PrefsStorage = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): PrefsStorage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The device's remembered Press-mode preferences. Per device is the right scope: the active platen
 * changes per job at THIS press, and the iPad is the station. Anything unreadable — a private window,
 * blocked site data, a corrupted or older-shaped value — degrades to the defaults rather than breaking
 * the kiosk (the label-format pattern).
 */
export function loadPressPrefs(storage: PrefsStorage | undefined = defaultStorage()): PressPrefs {
  try {
    const raw = storage?.getItem(PRESS_PREFS_KEY);
    if (!raw) return DEFAULT_PRESS_PREFS;
    const parsed = PressPrefsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_PRESS_PREFS;
  } catch {
    return DEFAULT_PRESS_PREFS;
  }
}

export function savePressPrefs(
  prefs: PressPrefs,
  storage: PrefsStorage | undefined = defaultStorage(),
): void {
  try {
    storage?.setItem(PRESS_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // A station that cannot persist the choice still shows the right frame for this session.
  }
}

/** The platen a prefs object names (catalog or remembered custom), defaulting safely. */
export function platenFromPrefs(prefs: PressPrefs): Platen {
  const custom = prefs.customPlaten
    ? customPlaten(prefs.customPlaten.acrossIn, prefs.customPlaten.deepIn)
    : null;
  return platenById(prefs.platenId, custom);
}

/** The press's lasers: the operator's setup when there is one, else the starting setup for this platen. */
export function lasersFromPrefs(prefs: PressPrefs, platen: Platen): Laser[] {
  return prefs.lasers ?? defaultLasers(platen);
}
